import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { useWooCommercePermissions } from '../../hooks/useWooCommercePermissions';
import { useNavigate } from 'react-router-dom';
import './WooCommerceSettings.css';

interface Branch {
  _id: string;
  name: string;
}

interface Warehouse {
  _id: string;
  name: string;
}

interface InvoicePage {
  _id: string;
  name: string;
}

interface WooCommerceSettingsData {
  _id?: string;
  storeUrl: string;
  consumerKey: string;
  consumerSecret: string;
  syncEnabled: boolean;
  autoSync: boolean;
  syncInterval: number;
  syncDaysLimit: number;
  defaultInvoicePage: string;
  defaultInvoiceType: string;
  defaultBranch: string;
  defaultWarehouse: string;
  defaultPaymentMethod: string;
  productMappingMethod: string;
  orderStatusesToSync: string[];
  webhookSecret: string;
  notes: string;
  lastSyncDate?: Date;
  lastSyncStatus?: string;
}

const WooCommerceSettings: React.FC = () => {
  const { hasWooCommerceSettingsPermission } = useWooCommercePermissions();
  const navigate = useNavigate();
  const [settings, setSettings] = useState<WooCommerceSettingsData>({
    storeUrl: '',
    consumerKey: '',
    consumerSecret: '',
    syncEnabled: false,
    autoSync: false,
    syncInterval: 30,
    syncDaysLimit: 30,
    defaultInvoicePage: '',
    defaultInvoiceType: 'sales',
    defaultBranch: '',
    defaultWarehouse: '',
    defaultPaymentMethod: 'other',
    productMappingMethod: 'sku',
    orderStatusesToSync: ['processing', 'completed', 'on-hold'],
    webhookSecret: '',
    notes: ''
  });

  const [branches, setBranches] = useState<Branch[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [invoicePages, setInvoicePages] = useState<InvoicePage[]>([]);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    fetchSettings();
    fetchBranches();
    fetchWarehouses();
    fetchInvoicePages();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/woocommerce/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success && response.data.settings) {
        setSettings(response.data.settings);
      }
    } catch (error: any) {
      console.error('خطأ في جلب الإعدادات:', error);
      showMessage('error', 'فشل جلب الإعدادات');
    }
  };

  const fetchBranches = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/branches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBranches(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('خطأ في جلب الفروع:', error);
      setBranches([]);
    }
  };

  const fetchWarehouses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/warehouses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWarehouses(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('خطأ في جلب المستودعات:', error);
      setWarehouses([]);
    }
  };

  const fetchInvoicePages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/invoices/pages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoicePages(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('خطأ في جلب صفحات الفواتير:', error);
      setInvoicePages([]);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setSettings(prev => ({ ...prev, [name]: checked }));
    } else if (type === 'number') {
      setSettings(prev => ({ ...prev, [name]: parseInt(value) || 0 }));
    } else {
      setSettings(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleOrderStatusChange = (status: string) => {
    setSettings(prev => {
      const statuses = prev.orderStatusesToSync.includes(status)
        ? prev.orderStatusesToSync.filter(s => s !== status)
        : [...prev.orderStatusesToSync, status];
      return { ...prev, orderStatusesToSync: statuses };
    });
  };

  const handleTestConnection = async () => {
    // التحقق من إدخال البيانات المطلوبة
    if (!settings.storeUrl || !settings.consumerKey || !settings.consumerSecret) {
      showMessage('error', 'يرجى إدخال رابط المتجر و Consumer Key و Consumer Secret أولاً');
      return;
    }

    setTesting(true);
    setMessage(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/woocommerce/test-connection`,
        {
          storeUrl: settings.storeUrl,
          consumerKey: settings.consumerKey,
          consumerSecret: settings.consumerSecret
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        showMessage('success', response.data.message || 'تم الاتصال بنجاح! ✓');
      } else {
        showMessage('error', response.data.message || 'فشل الاتصال');
      }
    } catch (error: any) {
      showMessage('error', error.response?.data?.message || 'فشل اختبار الاتصال');
    } finally {
      setTesting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/woocommerce/settings`,
        settings,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        showMessage('success', 'تم حفظ الإعدادات بنجاح ✓');
        setSettings(response.data.settings);
      }
    } catch (error: any) {
      showMessage('error', error.response?.data?.message || 'فشل حفظ الإعدادات');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const handleDeleteSettings = async () => {
    if (!window.confirm('⚠️ هل أنت متأكد من حذف جميع إعدادات WooCommerce؟\n\nسيتم حذف:\n- معلومات الاتصال\n- إعدادات المزامنة\n- الإعدادات الافتراضية\n\nلن يتم حذف الطلبات المستوردة.')) {
      return;
    }

    setDeleting(true);
    setMessage(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        `${API_BASE_URL}/woocommerce/settings`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        showMessage('success', 'تم حذف الإعدادات بنجاح ✓');
        // إعادة تعيين الإعدادات للقيم الافتراضية
        setSettings({
          storeUrl: '',
          consumerKey: '',
          consumerSecret: '',
          syncEnabled: false,
          autoSync: false,
          syncInterval: 30,
          syncDaysLimit: 30,
          defaultInvoicePage: '',
          defaultInvoiceType: 'sales',
          defaultBranch: '',
          defaultWarehouse: '',
          defaultPaymentMethod: 'other',
          productMappingMethod: 'sku',
          orderStatusesToSync: ['processing', 'completed', 'on-hold'],
          webhookSecret: '',
          notes: ''
        });
        
        // الانتقال إلى صفحة الطلبات بعد 2 ثانية
        setTimeout(() => {
          navigate('/invoices/woocommerce/orders');
        }, 2000);
      }
    } catch (error: any) {
      showMessage('error', error.response?.data?.message || 'فشل حذف الإعدادات');
    } finally {
      setDeleting(false);
    }
  };

  const orderStatuses = [
    { value: 'pending', label: 'قيد الانتظار' },
    { value: 'processing', label: 'قيد المعالجة' },
    { value: 'on-hold', label: 'معلق' },
    { value: 'completed', label: 'مكتمل' },
    { value: 'shipped', label: 'تم الشحن' },
    { value: 'cancelled', label: 'ملغي' },
    { value: 'refunded', label: 'مسترد' },
    { value: 'failed', label: 'فاشل' }
  ];

  return (
    <div className="woocommerce-settings-container">
      <div className="settings-header">
        <h2>⚙️ إعدادات مزامنة WooCommerce</h2>
        <p className="settings-description">
          قم بربط متجر WooCommerce الخاص بك لاستيراد الطلبات تلقائياً وتحويلها إلى فواتير
        </p>
      </div>

      {message && (
        <div className={`alert alert-${message.type}`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit} className="settings-form">
        {/* معلومات الاتصال */}
        <div className="settings-section">
          <h3>🔗 معلومات الاتصال</h3>
          
          <div className="form-group">
            <label htmlFor="storeUrl">رابط المتجر *</label>
            <input
              type="url"
              id="storeUrl"
              name="storeUrl"
              value={settings.storeUrl}
              onChange={handleInputChange}
              placeholder="https://example.com"
              required
            />
            <small>مثال: https://yourstore.com</small>
          </div>

          <div className="form-group">
            <label htmlFor="consumerKey">Consumer Key *</label>
            <input
              type="text"
              id="consumerKey"
              name="consumerKey"
              value={settings.consumerKey}
              onChange={handleInputChange}
              placeholder="ck_xxxxxxxxxxxxxxxx"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="consumerSecret">Consumer Secret *</label>
            <input
              type="password"
              id="consumerSecret"
              name="consumerSecret"
              value={settings.consumerSecret}
              onChange={handleInputChange}
              placeholder="cs_xxxxxxxxxxxxxxxx"
              required={!settings._id}
            />
            {settings._id && <small>اتركه فارغاً إذا كنت لا تريد تغييره</small>}
          </div>

          <button
            type="button"
            onClick={handleTestConnection}
            disabled={testing || !settings.storeUrl || !settings.consumerKey || !settings.consumerSecret}
            className="btn btn-secondary"
          >
            {testing ? '⏳ جاري الاختبار...' : '🔍 اختبار الاتصال'}
          </button>
        </div>

        {/* إعدادات المزامنة */}
        <div className="settings-section">
          <h3>🔄 إعدادات المزامنة</h3>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                name="syncEnabled"
                checked={settings.syncEnabled}
                onChange={handleInputChange}
              />
              <span>تفعيل المزامنة</span>
            </label>
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                name="autoSync"
                checked={settings.autoSync}
                onChange={handleInputChange}
                disabled={!settings.syncEnabled}
              />
              <span>المزامنة التلقائية</span>
            </label>
          </div>

          <div className="form-group">
            <label htmlFor="syncInterval">فترة المزامنة (بالدقائق)</label>
            <input
              type="number"
              id="syncInterval"
              name="syncInterval"
              value={settings.syncInterval}
              onChange={handleInputChange}
              min="5"
              max="1440"
              disabled={!settings.autoSync}
            />
          </div>

          <div className="form-group">
            <label htmlFor="syncDaysLimit">عدد الأيام للمزامنة</label>
            <input
              type="number"
              id="syncDaysLimit"
              name="syncDaysLimit"
              value={settings.syncDaysLimit}
              onChange={handleInputChange}
              min="0"
              max="365"
            />
            <small>
              {settings.syncDaysLimit === 0
                ? '⚠️ صفر = المزامنة من اللحظة الحالية فقط (بدون طلبات سابقة)'
                : `سيتم جلب الطلبات من آخر ${settings.syncDaysLimit} يوم`
              }
            </small>
          </div>

          <div className="form-group">
            <label>حالات الطلبات للمزامنة</label>
            <div className="checkbox-list">
              {orderStatuses.map(status => (
                <label key={status.value} className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={settings.orderStatusesToSync.includes(status.value)}
                    onChange={() => handleOrderStatusChange(status.value)}
                  />
                  <span>{status.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* الإعدادات الافتراضية */}
        <div className="settings-section">
          <h3>📋 الإعدادات الافتراضية للفواتير</h3>

          <div className="form-group">
            <label htmlFor="defaultInvoicePage">صفحة الفاتورة للتحويل (اختياري)</label>
            <select
              id="defaultInvoicePage"
              name="defaultInvoicePage"
              value={settings.defaultInvoicePage}
              onChange={handleInputChange}
            >
              <option value="">-- اختر صفحة الفاتورة --</option>
              {invoicePages.map(page => (
                <option key={page._id} value={page._id}>
                  {page.name}
                </option>
              ))}
            </select>
            <small>صفحة الفاتورة التي سيتم التحويل إليها عند تحويل الطلبات (اختياري)</small>
          </div>

          <div className="form-group">
            <label htmlFor="defaultInvoiceType">نوع الفاتورة الافتراضي</label>
            <select
              id="defaultInvoiceType"
              name="defaultInvoiceType"
              value={settings.defaultInvoiceType}
              onChange={handleInputChange}
            >
              <option value="sales">فاتورة مبيعات</option>
              <option value="salesReturn">فاتورة مرتجع مبيعات</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="defaultBranch">الفرع الافتراضي</label>
            <select
              id="defaultBranch"
              name="defaultBranch"
              value={settings.defaultBranch}
              onChange={handleInputChange}
            >
              <option value="">-- اختر الفرع --</option>
              {branches.map(branch => (
                <option key={branch._id} value={branch._id}>
                  {branch.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="defaultWarehouse">المستودع الافتراضي</label>
            <select
              id="defaultWarehouse"
              name="defaultWarehouse"
              value={settings.defaultWarehouse}
              onChange={handleInputChange}
            >
              <option value="">-- اختر المستودع --</option>
              {warehouses.map(warehouse => (
                <option key={warehouse._id} value={warehouse._id}>
                  {warehouse.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="defaultPaymentMethod">طريقة الدفع الافتراضية</label>
            <select
              id="defaultPaymentMethod"
              name="defaultPaymentMethod"
              value={settings.defaultPaymentMethod}
              onChange={handleInputChange}
            >
              <option value="cash">نقدي</option>
              <option value="card">بطاقة</option>
              <option value="bank">تحويل بنكي</option>
              <option value="other">أخرى</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="productMappingMethod">طريقة ربط المنتجات</label>
            <select
              id="productMappingMethod"
              name="productMappingMethod"
              value={settings.productMappingMethod}
              onChange={handleInputChange}
            >
              <option value="sku">حسب SKU (الباركود)</option>
              <option value="name">حسب الاسم</option>
              <option value="manual">يدوي</option>
            </select>
            <small>
              {settings.productMappingMethod === 'sku' && 'سيتم ربط المنتجات تلقائياً باستخدام SKU'}
              {settings.productMappingMethod === 'name' && 'سيتم ربط المنتجات تلقائياً باستخدام الاسم'}
              {settings.productMappingMethod === 'manual' && 'ستحتاج لربط المنتجات يدوياً'}
            </small>
          </div>
        </div>

        {/* Webhook */}
        <div className="settings-section">
          <h3>🔔 إعدادات Webhook</h3>
          
          <div className="form-group">
            <label htmlFor="webhookSecret">مفتاح Webhook السري (اختياري)</label>
            <input
              type="text"
              id="webhookSecret"
              name="webhookSecret"
              value={settings.webhookSecret}
              onChange={handleInputChange}
              placeholder="أدخل مفتاح سري للتحقق من Webhook"
            />
          </div>

          <div className="webhook-url-info">
            <label>رابط Webhook:</label>
            <code>{window.location.origin}/api/woocommerce/webhook</code>
            <small>استخدم هذا الرابط في إعدادات Webhook في WooCommerce</small>
          </div>
        </div>

        {/* ملاحظات */}
        <div className="settings-section">
          <h3>📝 ملاحظات</h3>
          
          <div className="form-group">
            <textarea
              id="notes"
              name="notes"
              value={settings.notes}
              onChange={handleInputChange}
              rows={4}
              placeholder="أضف أي ملاحظات إضافية..."
            />
          </div>
        </div>

        {/* معلومات آخر مزامنة */}
        {settings.lastSyncDate && (
          <div className="settings-section last-sync-info">
            <h3>ℹ️ معلومات آخر مزامنة</h3>
            <p>
              <strong>التاريخ:</strong> {new Date(settings.lastSyncDate).toLocaleString('ar-SA')}
            </p>
            <p>
              <strong>الحالة:</strong>{' '}
              <span className={`status-badge status-${settings.lastSyncStatus}`}>
                {settings.lastSyncStatus === 'success' ? '✓ نجحت' : '✗ فشلت'}
              </span>
            </p>
          </div>
        )}

        {/* أزرار الحفظ والحذف */}
        <div className="form-actions">
          {hasWooCommerceSettingsPermission('save_settings') && (
            <button type="submit" disabled={loading || deleting} className="btn btn-primary">
              {loading ? '⏳ جاري الحفظ...' : '💾 حفظ الإعدادات'}
            </button>
          )}
          
          {hasWooCommerceSettingsPermission('delete_settings') && settings._id && (
            <button
              type="button"
              onClick={handleDeleteSettings}
              disabled={loading || deleting}
              className="btn btn-danger"
              style={{ marginRight: '10px' }}
            >
              {deleting ? '⏳ جاري الحذف...' : '🗑️ حذف الإعدادات'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default WooCommerceSettings;
